import { Injectable, Inject, RendererFactory2, Renderer2 } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { ISeoDocument } from '../models/documents.model';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private renderer: Renderer2;

  /** Track injected schema <script> elements so we can clean them up on navigation */
  private schemaScripts: HTMLScriptElement[] = [];

  constructor(
    private titleService: Title,
    private meta: Meta,
    private rendererFactory: RendererFactory2,
    private router: Router,
    @Inject(DOCUMENT) private doc: Document
  ) {
    // RendererFactory2.createRenderer(null, null) gives us a DOM renderer
    // that works in both browser and SSR contexts.
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  // ================================================================
  // Public API
  // ================================================================

  /**
   * Apply all SEO data from an ISeoDocument onto the page.
   * Call this once in your page component's ngOnInit / afterNextRender.
   */
  setPageSeo(seoDoc: Partial<ISeoDocument>): void {
    if (!seoDoc) return;

    // ---- HEAD tags ----
    if( seoDoc.meta_title ) this.setTitle(seoDoc.meta_title);
    if(  seoDoc.meta_description ) this.setMetaDescription(seoDoc.meta_description);
    if( seoDoc.meta_keywords ) this.setMetaKeywords(seoDoc.meta_keywords);
    if(seoDoc.canonical_url) this.setCanonical(seoDoc.canonical_url);
    if (seoDoc.language || seoDoc.dir) this.setLanguageAndDir(seoDoc.language || '', seoDoc.dir || '');
    if (seoDoc.is_active !== undefined && seoDoc.is_active !== null ) this.setRobots(seoDoc.is_active);
    if (seoDoc.meta_title && seoDoc.meta_description) this.setOpenGraph(seoDoc.meta_title, seoDoc.meta_description);
    if (seoDoc.meta_title && seoDoc.meta_description) this.setTwitterCard(seoDoc.meta_title, seoDoc.meta_description);

    // ---- BODY tags ----
    if (seoDoc.schema_json) this.setSchemaJsonLd(seoDoc.schema_json);
  }

  /**
   * Remove all SEO artifacts injected by this service (call on component destroy).
   */
  clearPageSeo(): void {
    this.removeSchemaScripts();
  }

  // ================================================================
  // HEAD helpers
  // ================================================================

  private setTitle(title: string): void {
    if (title) {
      this.titleService.setTitle(title);
    }
  }

  private setMetaDescription(description: string): void {
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    } else {
      this.meta.removeTag('name="description"');
    }
  }

  private setMetaKeywords(keywords: string): void {
    if (keywords) {
      this.meta.updateTag({ name: 'keywords', content: keywords });
    } else {
      this.meta.removeTag('name="keywords"');
    }
  }

  private setCanonical(url: string): void {
    // Remove any existing canonical
    const existing = this.doc.querySelector('link[rel="canonical"]');
    if (existing) {
      this.renderer.removeChild(this.doc.head, existing);
    }

    if (url) {
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', url);
      this.renderer.appendChild(this.doc.head, link);
    }
  }

  private setLanguageAndDir(language: string, dir: string): void {
    const html = this.doc.documentElement;
    if (language) {
      this.renderer.setAttribute(html, 'lang', language);
    }
    if (dir) {
      this.renderer.setAttribute(html, 'dir', dir);
    }
  }

  private setRobots(isActive: boolean): void {
    if (!isActive) {
      // Tell search engines not to index inactive pages
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    }
  }

  // ---- Open Graph (Facebook / LinkedIn / WhatsApp) ----

  private setOpenGraph(title: string, description: string): void {
    if (title) {
      this.meta.updateTag({ property: 'og:title', content: title });
    }
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    // og:url — use the canonical if available, else the current route
    const ogUrl = this.doc.querySelector('link[rel="canonical"]')?.getAttribute('href')
      || (this.doc.location?.origin + this.router.url);
    this.meta.updateTag({ property: 'og:url', content: ogUrl });

    if (description) {
      this.meta.updateTag({ property: 'og:description', content: description });
    }
  }

  // ---- Twitter Card ----

  private setTwitterCard(title: string, description: string): void {
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    if (title) {
      this.meta.updateTag({ name: 'twitter:title', content: title });
    }
    if (description) {
      this.meta.updateTag({ name: 'twitter:description', content: description });
    }
  }

  // ================================================================
  // BODY helpers — JSON-LD Schema
  // ================================================================

  /**
   * Injects one <script type="application/ld+json"> per schema object
   * into the document body so Google / Bing can read structured data.
   */
  private setSchemaJsonLd(schemas: any[]): void {
    // Clean up any previously injected scripts first
    this.removeSchemaScripts();

    if (!schemas || schemas.length === 0) return;

    for (const schema of schemas) {
      const script: HTMLScriptElement = this.renderer.createElement('script');
      this.renderer.setAttribute(script, 'type', 'application/ld+json');
      const text = this.renderer.createText(JSON.stringify(schema));
      this.renderer.appendChild(script, text);
      this.renderer.appendChild(this.doc.body, script);
      this.schemaScripts.push(script);
    }
  }

  private removeSchemaScripts(): void {
    for (const script of this.schemaScripts) {
      try {
        this.renderer.removeChild(this.doc.body, script);
      } catch {
        // Script may already be removed (e.g. during SSR teardown)
      }
    }
    this.schemaScripts = [];
  }
}
